import { useState } from 'react'
import type { Conversation } from '@assembly/slack'
import { useActions } from '../data/actions'
import { useBookmarks, useConversation, useConversationName, useMembers, usePins, usePresence, useUser, useUsers, displayName, fullName } from '../data/hooks'
import { useMe, useWorkspace } from '../data/workspace'
import { useUi } from '../state/ui'
import { usePlatform } from '../platform'
import { formatDateTime, formatShortDate, localTimeIn } from '../format'
import { ConversationIcon } from './ChannelView'
import { Icon } from './Icon'
import { ExternalLink, Files, Mrkdwn } from './MessageContent'
import { Avatar, Emoji, IconButton, Menu, Spinner, UserAvatar, anchorFrom, authorName, type Anchor } from './common'

function EditableField({
  label,
  value,
  placeholder,
  onSave,
  multiline,
  readOnly,
  render,
}: {
  label: string
  value: string
  placeholder: string
  onSave: (v: string) => Promise<unknown>
  multiline?: boolean
  readOnly?: boolean
  render?: (v: string) => React.ReactNode
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const [saving, setSaving] = useState(false)
  const save = async () => {
    setSaving(true)
    try {
      await onSave(draft.trim())
      setEditing(false)
    } catch {
      /* toast shown by action */
    } finally {
      setSaving(false)
    }
  }
  return (
    <div className="detail-field">
      <div className="detail-field-header">
        <strong>{label}</strong>
        {!readOnly && !editing && (
          <button type="button" className="link-button" onClick={() => (setDraft(value), setEditing(true))}>
            Edit
          </button>
        )}
      </div>
      {editing ? (
        <div className="detail-edit">
          {multiline ? (
            <textarea autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} />
          ) : (
            <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && save()} />
          )}
          <div className="row-end">
            <button type="button" className="button" onClick={() => setEditing(false)}>
              Cancel
            </button>
            <button type="button" className="button primary" onClick={save} disabled={saving}>
              {saving ? <Spinner size={14} /> : 'Save'}
            </button>
          </div>
        </div>
      ) : value ? (
        render ? render(value) : <div>{value}</div>
      ) : (
        <div className="muted">{placeholder}</div>
      )}
    </div>
  )
}

function AboutTab({ conv }: { conv: Conversation }) {
  const actions = useActions()
  const creator = useUser(conv.creator)
  const platform = usePlatform()
  const { account } = useWorkspace()
  const isChannel = !conv.is_im && !conv.is_mpim
  return (
    <div className="details-tab">
      {isChannel && (
        <EditableField
          label="Channel name"
          value={conv.name ?? ''}
          placeholder=""
          readOnly={conv.is_general}
          onSave={(v) => actions.renameChannel(conv.id, v.replace(/^#/, '').toLowerCase().replace(/\s+/g, '-'))}
          render={(v) => (
            <div>
              <ConversationIcon conv={conv} size={14} /> {v}
            </div>
          )}
        />
      )}
      <EditableField label="Topic" value={conv.topic?.value ?? ''} placeholder="Add a topic" onSave={(v) => actions.setTopic(conv.id, v)} render={(v) => <Mrkdwn text={v} />} />
      {isChannel && (
        <EditableField
          label="Description"
          value={conv.purpose?.value ?? ''}
          placeholder="Add a description"
          multiline
          onSave={(v) => actions.setPurpose(conv.id, v)}
          render={(v) => <Mrkdwn text={v} />}
        />
      )}
      {conv.created && (
        <div className="detail-field">
          <strong>Created</strong>
          <div className="muted">
            {creator ? `By ${displayName(creator)} on ` : ''}
            {formatShortDate(conv.created)}
          </div>
        </div>
      )}
      <div className="detail-field">
        <strong>Channel ID</strong>
        <div className="row">
          <code>{conv.id}</code>
          <IconButton icon="copy" label="Copy ID" size={14} onClick={() => navigator.clipboard?.writeText(conv.id)} />
          {account.teamDomain && (
            <IconButton icon="external" label="Open in Slack" size={14} onClick={() => platform.openExternal(`https://${account.teamDomain}.slack.com/archives/${conv.id}`)} />
          )}
        </div>
      </div>
    </div>
  )
}

function MemberRow({ id, conv }: { id: string; conv: Conversation }) {
  const user = useUser(id)
  const me = useMe()
  const actions = useActions()
  const openPanel = useUi((s) => s.openPanel)
  const openModal = useUi((s) => s.openModal)
  const [menu, setMenu] = useState<Anchor | null>(null)
  return (
    <div className="member-row">
      <button type="button" className="member-main" onClick={() => openPanel({ type: 'profile', user: id })}>
        <Avatar user={user} size={32} />
        <span>
          <strong>
            {displayName(user, id)}
            {id === me && ' (you)'}
          </strong>
          {user && fullName(user) !== displayName(user) && <span className="muted"> {fullName(user)}</span>}
        </span>
      </button>
      {!conv.is_im && !conv.is_mpim && (
        <IconButton icon="more" label="Member actions" size={16} onClick={(e) => setMenu(anchorFrom(e))} />
      )}
      {menu && (
        <Menu
          anchor={menu}
          onClose={() => setMenu(null)}
          items={[
            { label: 'View profile', icon: 'user', onSelect: () => openPanel({ type: 'profile', user: id }) },
            id !== me && { label: 'Message', icon: 'thread', onSelect: () => actions.openDirectMessage([id]) },
            id === me
              ? !conv.is_general && { label: 'Leave channel', icon: 'door', danger: true, onSelect: () => actions.leaveChannel(conv.id) }
              : {
                  label: `Remove from #${conv.name}`,
                  icon: 'x',
                  danger: true,
                  onSelect: () =>
                    openModal({
                      type: 'confirm',
                      title: `Remove ${displayName(user)}?`,
                      body: `They’ll no longer be able to see or send messages in #${conv.name}.`,
                      confirm: 'Remove',
                      danger: true,
                      onConfirm: () => actions.kick(conv.id, id),
                    }),
                },
          ]}
        />
      )}
    </div>
  )
}

function MembersTab({ conv }: { conv: Conversation }) {
  const { data: members, isLoading } = useMembers(conv.id)
  const { data: users } = useUsers()
  const openModal = useUi((s) => s.openModal)
  const [filter, setFilter] = useState('')
  const q = filter.toLowerCase()
  const list = (members ?? []).filter((id) => {
    if (!q) return true
    const u = users?.get(id)
    return displayName(u, id).toLowerCase().includes(q) || fullName(u).toLowerCase().includes(q)
  })
  return (
    <div className="details-tab">
      <input className="search-input" placeholder="Find members" value={filter} onChange={(e) => setFilter(e.target.value)} />
      {!conv.is_im && !conv.is_mpim && (
        <button type="button" className="member-row add" onClick={() => openModal({ type: 'invite', channel: conv.id })}>
          <span className="add-icon">
            <Icon name="userPlus" size={16} />
          </span>
          <strong>Add people</strong>
        </button>
      )}
      {isLoading && <Spinner />}
      {list.map((id) => (
        <MemberRow key={id} id={id} conv={conv} />
      ))}
    </div>
  )
}

function PinsTab({ conv }: { conv: Conversation }) {
  const { data: pins, isLoading } = usePins(conv.id)
  const actions = useActions()
  const openChannel = useUi((s) => s.openChannel)
  if (isLoading) return <Spinner />
  if (!pins?.length) {
    return (
      <div className="details-tab empty-small">
        <Icon name="pin" size={28} />
        <p className="muted">No pinned items yet. Pin important messages from the message menu.</p>
      </div>
    )
  }
  return (
    <div className="details-tab">
      {pins.map((p, i) => (
        <PinnedItem key={i} item={p} onJump={() => p.message && openChannel(conv.id, { highlight: p.message.ts })} onUnpin={() => p.message && actions.togglePin(conv.id, { ...p.message, pinned_to: [conv.id] })} />
      ))}
    </div>
  )
}

function PinnedItem({ item, onJump, onUnpin }: { item: { message?: any; file?: any; created: number }; onJump: () => void; onUnpin: () => void }) {
  const user = useUser(item.message?.user)
  return (
    <div className="pinned-item">
      <div className="pinned-head">
        <UserAvatar id={item.message?.user} size={20} />
        <strong>{item.message ? authorName(item.message, user) : 'File'}</strong>
        <span className="muted">{item.message ? formatDateTime(item.message.ts) : ''}</span>
      </div>
      {item.message?.text && <Mrkdwn text={item.message.text} />}
      {item.message?.files && <Files files={item.message.files} />}
      {item.file && <Files files={[item.file]} />}
      <div className="row-end">
        {item.message && (
          <button type="button" className="link-button" onClick={onJump}>
            View in channel
          </button>
        )}
        {item.message && (
          <button type="button" className="link-button danger" onClick={onUnpin}>
            Unpin
          </button>
        )}
      </div>
    </div>
  )
}

function BookmarksTab({ conv }: { conv: Conversation }) {
  const { data: bookmarks, isLoading, error } = useBookmarks(conv.id)
  const actions = useActions()
  const openModal = useUi((s) => s.openModal)
  if (isLoading) return <Spinner />
  return (
    <div className="details-tab">
      {error && <p className="muted">Bookmarks aren’t available for this conversation.</p>}
      {(bookmarks ?? []).map((b) => (
        <div key={b.id} className="bookmark-row">
          {b.emoji ? <Emoji name={b.emoji.replace(/:/g, '')} /> : <Icon name="link" size={16} />}
          <ExternalLink href={b.link}>{b.title}</ExternalLink>
          <span className="grow" />
          <IconButton icon="trash" label="Remove bookmark" size={15} onClick={() => actions.removeBookmark(conv.id, b.id)} />
        </div>
      ))}
      {!error && (
        <button type="button" className="button" onClick={() => openModal({ type: 'addBookmark', channel: conv.id })}>
          <Icon name="plus" size={15} /> Add a bookmark
        </button>
      )}
    </div>
  )
}

function SettingsTab({ conv }: { conv: Conversation }) {
  const actions = useActions()
  const openModal = useUi((s) => s.openModal)
  const isChannel = !conv.is_im && !conv.is_mpim
  return (
    <div className="details-tab settings-tab">
      {isChannel && !conv.is_general && conv.is_member && (
        <button type="button" className="settings-row" onClick={() => actions.leaveChannel(conv.id)}>
          <Icon name="door" /> Leave channel
        </button>
      )}
      {!isChannel && (
        <button type="button" className="settings-row" onClick={() => actions.closeConversation(conv.id)}>
          <Icon name="x" /> Close conversation
        </button>
      )}
      {isChannel &&
        !conv.is_general &&
        (conv.is_archived ? (
          <button type="button" className="settings-row" onClick={() => actions.unarchiveChannel(conv.id)}>
            <Icon name="archive" /> Unarchive channel
          </button>
        ) : (
          <button
            type="button"
            className="settings-row danger"
            onClick={() =>
              openModal({
                type: 'confirm',
                title: `Archive #${conv.name}?`,
                body: 'Nobody will be able to send messages to it. The history stays searchable and you can unarchive it any time.',
                confirm: 'Archive channel',
                danger: true,
                onConfirm: () => actions.archiveChannel(conv.id),
              })
            }
          >
            <Icon name="archive" /> Archive channel
          </button>
        ))}
    </div>
  )
}

export function ChannelDetailsPanel({ channel, tab }: { channel: string; tab?: 'about' | 'members' | 'pins' | 'bookmarks' | 'settings' }) {
  const { data: conv } = useConversation(channel)
  const name = useConversationName()
  const openPanel = useUi((s) => s.openPanel)
  const current = tab ?? 'about'
  if (!conv) return null
  const tabs = (['about', 'members', 'pins', 'bookmarks', 'settings'] as const).filter((t) => !(conv.is_im && (t === 'members' || t === 'bookmarks')))
  return (
    <aside className="side-panel details-panel">
      <header className="panel-header">
        <h3>
          <ConversationIcon conv={conv} size={15} /> {name(conv)}
        </h3>
        <IconButton icon="x" label="Close details" onClick={() => openPanel(null)} />
      </header>
      <nav className="tabs">
        {tabs.map((t) => (
          <button key={t} type="button" className={t === current ? 'active' : ''} onClick={() => openPanel({ type: 'details', channel, tab: t })}>
            {t[0].toUpperCase() + t.slice(1)}
          </button>
        ))}
      </nav>
      <div className="panel-scroll">
        {current === 'about' && <AboutTab conv={conv} />}
        {current === 'members' && <MembersTab conv={conv} />}
        {current === 'pins' && <PinsTab conv={conv} />}
        {current === 'bookmarks' && <BookmarksTab conv={conv} />}
        {current === 'settings' && <SettingsTab conv={conv} />}
      </div>
    </aside>
  )
}

export function ProfilePanel({ userId }: { userId: string }) {
  const user = useUser(userId)
  const me = useMe()
  const actions = useActions()
  const openPanel = useUi((s) => s.openPanel)
  const openModal = useUi((s) => s.openModal)
  const { data: presence } = usePresence(userId)
  const local = localTimeIn(user?.tz)
  const p = user?.profile
  return (
    <aside className="side-panel profile-panel">
      <header className="panel-header">
        <h3>Profile</h3>
        <IconButton icon="x" label="Close profile" onClick={() => openPanel(null)} />
      </header>
      <div className="panel-scroll">
        {!user ? (
          <Spinner />
        ) : (
          <div className="profile">
            <Avatar user={user} size={220} className="profile-photo" />
            <h2>
              {fullName(user)} <span className={`presence inline ${presence === 'active' ? 'active' : ''}`} />
            </h2>
            {p?.display_name && p.display_name !== fullName(user) && <div className="muted">@{p.display_name}</div>}
            {p?.title && <div>{p.title}</div>}
            {(p?.status_text || p?.status_emoji) && (
              <div className="profile-status">
                {p.status_emoji && <Emoji name={p.status_emoji.replace(/:/g, '')} />} {p.status_text}
              </div>
            )}
            {local && (
              <div className="muted">
                <Icon name="clock" size={14} /> {local} local time
              </div>
            )}
            <div className="profile-actions">
              {userId === me ? (
                <>
                  <button type="button" className="button" onClick={() => openModal({ type: 'editProfile' })}>
                    <Icon name="pencil" size={15} /> Edit profile
                  </button>
                  <button type="button" className="button" onClick={() => openModal({ type: 'setStatus' })}>
                    <Icon name="smile" size={15} /> Set status
                  </button>
                </>
              ) : (
                <button type="button" className="button primary" onClick={() => actions.openDirectMessage([userId])}>
                  <Icon name="thread" size={15} /> Message
                </button>
              )}
              <IconButton icon="copy" label="Copy member ID" onClick={() => navigator.clipboard?.writeText(userId)} />
            </div>
            <dl className="profile-fields">
              {p?.email && (
                <>
                  <dt>Email</dt>
                  <dd>
                    <ExternalLink href={`mailto:${p.email}`}>{p.email}</ExternalLink>
                  </dd>
                </>
              )}
              {p?.phone && (
                <>
                  <dt>Phone</dt>
                  <dd>
                    <ExternalLink href={`tel:${p.phone}`}>{p.phone}</ExternalLink>
                  </dd>
                </>
              )}
              {user.tz_label && (
                <>
                  <dt>Time zone</dt>
                  <dd>{user.tz_label}</dd>
                </>
              )}
            </dl>
          </div>
        )}
      </div>
    </aside>
  )
}
